import { EMAIL_ACTION_TYPES } from "../../shared/flowTouchpoints";

/**
 * How a task relates to the contact's warmup flow:
 *   none     – the contact is in no flow; nothing follows automatically.
 *   fulfils  – the task IS the flow's current step; finishing it advances the flow.
 *   extra    – a manual step on the side; the flow keeps its own schedule.
 */

const TASK_CHANNEL = { send_mail: "email", call_back: "phone", book_meeting: "meeting" };

const STEP_LABELS = {
  email_sent: "Send email", newsletter: "Send newsletter",
  phone_call: "Call", sales_meeting: "Meeting",
  linkedin_message: "LinkedIn message", linkedin_connect: "LinkedIn connect",
  instagram_dm: "Instagram DM", instagram_follow: "Instagram follow",
  whatsapp: "WhatsApp", whatsapp_message: "WhatsApp",
  log_notes: "Log notes", condition: "Decision", end: "Flow ends",
  auto_send_email: "Auto email", auto_check_reply: "Wait for reply",
};

const typeOf = (step) => String(step?.type || step?.nodeType || "").toLowerCase();

export const stepChannel = (step) => {
  if (!step) return null;
  const t = typeOf(step);
  if (step.nodeType === "auto_send_email" || step.nodeType === "auto_check_reply") return "auto";
  if (EMAIL_ACTION_TYPES.includes(t) || t.includes("email")) return "email";
  if (t.includes("phone") || t.includes("call")) return "phone";
  if (t.includes("meeting")) return "meeting";
  if (t.includes("linkedin")) return "linkedin";
  if (t.includes("instagram")) return "instagram";
  if (t.includes("whatsapp")) return "whatsapp";
  if (step.nodeType === "delay" || t === "delay") return "wait";
  if (step.nodeType === "end" || t === "end") return "end";
  if (step.nodeType === "condition" || t === "condition") return "decision";
  if (step.nodeType === "log_notes") return "notes";
  return "other";
};

const CHANNEL_ICON = {
  email: "fa-paper-plane", phone: "fa-phone", meeting: "fa-calendar-check",
  linkedin: "fa-linkedin", instagram: "fa-instagram", whatsapp: "fa-whatsapp",
  wait: "fa-hourglass-half", end: "fa-flag-checkered", decision: "fa-code-branch",
  notes: "fa-clipboard-list", auto: "fa-robot", other: "fa-bolt",
};

export const stepLabel = (step) => {
  if (!step) return "";
  if (stepChannel(step) === "decision" && step.customQuestion) return step.customQuestion;
  if (step.label) return step.label;
  const t = step.type || step.nodeType || "";
  return STEP_LABELS[t] || (t ? t.replace(/_/g, " ").replace(/^\w/, c => c.toUpperCase()) : "Next step");
};

const describe = (step) => step && {
  label: stepLabel(step),
  channel: stepChannel(step),
  icon: CHANNEL_ICON[stepChannel(step)] || CHANNEL_ICON.other,
  brand: ["linkedin", "instagram", "whatsapp"].includes(stepChannel(step)),
};

const indexOfStep = (flow, step) => flow.steps.findIndex(s => s.id === step.id);

/** Next step by the visual edges, falling back to array order like the engine. */
export const followingStep = (flow, step) => {
  const edges = flow?.visualData?.edges || [];
  const out = edges.find(e => e.source === step?.id);
  if (out) return flow.steps.find(s => s.id === out.target) || null;
  const idx = indexOfStep(flow, step);
  return idx >= 0 ? flow.steps[idx + 1] || null : null;
};

const isPassThrough = (step) =>
  step?.nodeType === "tag" || (step?.nodeType === "delay" && !(parseInt(step.delayDays, 10) > 0));

/** The first step after `step` that waits for a person or for time. */
const upcomingAfter = (flow, step) => {
  let next = followingStep(flow, step);
  let delayDays = 0;
  for (let hops = 0; next && hops < 12; hops++) {
    if (next.nodeType === "delay" && parseInt(next.delayDays, 10) > 0) {
      delayDays += parseInt(next.delayDays, 10);
      next = followingStep(flow, next);
      continue;
    }
    if (!isPassThrough(next)) break;
    next = followingStep(flow, next);
  }
  return next ? { ...describe(next), delayDays } : { label: "Flow ends", channel: "end", icon: CHANNEL_ICON.end, delayDays };
};

export const resolveTaskFlow = (task, contact, flows, now = Date.now()) => {
  const state = contact?.warmupFlow;
  if (!state || !(state.currentStepIndex >= 0) || contact.globalOptOut === true) return { kind: "none" };
  const flow = (flows || []).find(f => f.id === state.flowId);
  const current = flow?.steps?.[state.currentStepIndex];
  if (!current) return { kind: "none" };

  const readyAt = state.nextStepAvailableAt || state.startedAt || 0;
  const waiting = now < readyAt;
  const step = current.nodeType === "delay" ? followingStep(flow, current) || current : current;

  const base = {
    flow,
    flowId: flow.id,
    flowName: state.flowName || flow.name || "Flow",
    fromIndex: state.currentStepIndex,
    stepIndex: indexOfStep(flow, step),
    step: describe(step),
  };

  const channel = TASK_CHANNEL[task?.action];
  if (!waiting && channel && stepChannel(step) === channel) {
    return { ...base, kind: "fulfils", after: upcomingAfter(flow, step) };
  }
  return { ...base, kind: "extra", dueAt: waiting ? readyAt : null };
};
